
import { Component, Vue, Watch } from 'vue-property-decorator'
import {
  NOTICE_KEY,
  getPurchaseNoticeList,
  updatePurchaseNotice,
  PurchaseNotice,
  PurchaseNoticeSection
} from '@/api/purchaseNotice'
import { UserModule } from '@/store/modules/user'

@Component({
  name: 'PurchaseNoticePage',
  beforeRouteLeave(to: any, from: any, next: any) {
    ;(this as any).handleRouteLeave(next)
  }
})
export default class extends Vue {
  private saving: boolean = false
  private form: PurchaseNotice = this.emptyForm()
  private savedSnapshot: string = ''

  get currentStoreId() {
    const sid = UserModule.storeId
    if (!sid) return null
    const n = Number(sid)
    return Number.isNaN(n) ? null : n
  }

  get currentStoreLabel(): string {
    if (this.currentStoreId == null) return '未选择商户（请先在顶栏选店）'
    return `商户 #${this.currentStoreId}`
  }

  @Watch('currentStoreId')
  private onStoreChange() {
    this.loadList()
  }

  created() {
    this.loadList()
  }

  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  }

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  }

  private handleRouteLeave(next: any) {
    if (!this.hasUnsavedChanges()) {
      next()
      return
    }
    this.$confirm('内容未保存，确定要放弃修改吗？', '提示', {
      type: 'warning',
      distinguishCancelAndClose: true,
      confirmButtonText: '放弃更改',
      cancelButtonText: '保存',
      confirmButtonClass: 'el-button--default',
      cancelButtonClass: 'el-button--primary'
    })
      .then(() => next())
      .catch((action: string) => {
        if (action === 'cancel') {
          this.handleSave()
        }
        next(false)
      })
  }

  private emptyForm(): PurchaseNotice {
    return {
      noticeKey: NOTICE_KEY,
      title: '购买须知',
      status: 1,
      sections: [
        {
          title: '',
          items: [{ label: '1.', text: '' }]
        }
      ]
    }
  }

  private async loadList() {
    if (this.currentStoreId == null) {
      this.form = this.emptyForm()
      this.markSaved()
      return
    }
    try {
      const res: any = await getPurchaseNoticeList(this.currentStoreId)
      const list: PurchaseNotice[] = (res && res.data && res.data.data) || []
      const row = list.find((it) => it && it.noticeKey === NOTICE_KEY) || list[0]
      if (!row) {
        this.form = this.emptyForm()
        this.markSaved()
        return
      }
      this.form = {
        id: row.id,
        storeId: this.currentStoreId,
        noticeKey: NOTICE_KEY,
        title: row.title || '购买须知',
        status: row.status == null ? 1 : row.status,
        sections: Array.isArray(row.sections) && row.sections.length
          ? row.sections.map((s) => ({
              title: (s && s.title) || '',
              items: Array.isArray(s && s.items)
                ? s.items.map((it) => ({
                    label: (it && it.label) || '',
                    text: (it && it.text) || ''
                  }))
                : []
            }))
          : this.emptyForm().sections
      }
      this.markSaved()
    } catch (err: any) {
      this.$message.error('加载失败：' + ((err && err.message) || '未知错误'))
    }
  }

  private normalizeForm(form?: PurchaseNotice | null) {
    const current = form || this.emptyForm()
    return {
      noticeKey: NOTICE_KEY,
      title: (current.title || '').trim(),
      status: current.status == null ? 1 : current.status,
      sections: Array.isArray(current.sections)
        ? current.sections.map((section) => ({
            title: ((section && section.title) || '').trim(),
            items: Array.isArray(section && section.items)
              ? section.items.map((item) => ({
                  label: ((item && item.label) || '').trim(),
                  text: ((item && item.text) || '').trim()
                }))
              : []
          }))
        : []
    }
  }

  private snapshotOf(form?: PurchaseNotice | null): string {
    return JSON.stringify(this.normalizeForm(form))
  }

  private markSaved() {
    this.savedSnapshot = this.snapshotOf(this.form)
  }

  private hasUnsavedChanges(): boolean {
    return this.snapshotOf(this.form) !== this.savedSnapshot
  }

  private handleBeforeUnload(e: BeforeUnloadEvent) {
    if (!this.hasUnsavedChanges()) return
    e.preventDefault()
    e.returnValue = ''
  }

  private addSection() {
    if (!this.form) return
    const n = this.form.sections.length + 1
    this.form.sections.push({
      title: `${n}、`,
      items: [{ label: `${n}.1`, text: '' }]
    })
  }

  private removeSection(si: number) {
    if (!this.form) return
    this.form.sections.splice(si, 1)
  }

  private addItem(si: number) {
    if (!this.form) return
    const section: PurchaseNoticeSection = this.form.sections[si]
    if (!section) return
    if (!section.items) this.$set(section, 'items', [])
    const n = section.items.length + 1
    section.items.push({ label: `${si + 1}.${n}`, text: '' })
  }

  private removeItem(si: number, ii: number) {
    if (!this.form) return
    const section = this.form.sections[si]
    if (!section || !section.items) return
    section.items.splice(ii, 1)
  }

  private handleSave() {
    const current = this.form
    if (!current) return
    if (this.currentStoreId == null) {
      this.$message.error('请先在顶栏选择商户')
      return
    }
    if (!current.title || !current.title.trim()) {
      this.$message.error('请填写大标题')
      return
    }
    if (!current.sections || !current.sections.length) {
      this.$message.error('请至少添加一个章节')
      return
    }
    this.saving = true
    updatePurchaseNotice({
      storeId: this.currentStoreId,
      noticeKey: NOTICE_KEY,
      title: current.title.trim(),
      status: current.status,
      sections: current.sections
    })
      .then((res: any) => {
        if (res && res.data && res.data.code === 1) {
          this.$message.success('保存成功')
          this.loadList()
        } else {
          this.$message.error((res.data && res.data.msg) || '保存失败')
        }
      })
      .catch((err: any) => {
        this.$message.error('保存失败：' + ((err && err.message) || '未知错误'))
      })
      .finally(() => {
        this.saving = false
      })
  }
}
